import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ShareDialog } from "@/components/share/share-dialog";
import { CardGridLoader } from "@/components/shared/page-loader";
import { useToast } from "@/components/ui/use-toast";
import { getHabit, checkInHabit } from "@/data/requests/habit-requests";
import { eachDayOfInterval, isSameDay } from "date-fns";
import {
  ArrowLeft,
  Flame,
  Target,
  TrendingUp,
  CheckCircle2,
  Calendar,
  Share2,
} from "lucide-react";

export function HabitDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: habitResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["habit", id],
    queryFn: () => getHabit(id),
    enabled: !!id,
  });

  const habit = habitResponse?.data;

  const checkInMutation = useMutation({
    mutationFn: () => checkInHabit(id, { note: "" }),
    onSuccess: async () => {
      // Invalidate and refetch immediately
      await queryClient.invalidateQueries({ queryKey: ["habit", id] });
      await queryClient.invalidateQueries({ queryKey: ["habits"] });
      await queryClient.refetchQueries({ queryKey: ["habit", id] });
      await queryClient.refetchQueries({ queryKey: ["habits"] });
      toast({
        title: t("habits.checkedIn"),
        description: t("habits.keepStreak"),
      });
    },
    onError: (error) => {
      const message = error.response?.data?.message || t("habits.checkInFailed");
      toast({
        title: t("common.error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <AppLayout title={t("habitDetail.title")} description={t("habitDetail.loading")}>
        <CardGridLoader count={3} />
      </AppLayout>
    );
  }

  if (isError || !habit) {
    return (
      <AppLayout title={t("habitDetail.title")}>
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>{t("habitDetail.notFound")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common.back")}
            </Button>
          </CardContent>
        </Card>
      </AppLayout>
    );
  }

  const streak = habit.streak || 0;
  const thisWeekCount = habit.thisWeekCount || 0;
  const completionRate = habit.completionRate || 0;

  // Contribution-style activity grid (from creation date to today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const createdAt = new Date(habit.createdAt);
  createdAt.setHours(0, 0, 0, 0);

  const activityDays =
    createdAt <= today
      ? eachDayOfInterval({ start: createdAt, end: today })
      : [];

  const hasCheckInOnDate = (date) => {
    if (!habit.logs) return false;
    return habit.logs.some((log) => {
      const loggedAt = new Date(log.loggedAt);
      return isSameDay(loggedAt, date);
    });
  };

  const cadenceLabelKey =
    habit.cadence === "weekdays"
      ? "habits.create.cadenceWeekdays"
      : habit.cadence === "weekends"
      ? "habits.create.cadenceWeekends"
      : habit.cadence === "weekly"
      ? "habits.create.cadenceWeekly"
      : habit.cadence === "custom"
      ? "habits.create.cadenceCustom"
      : "habits.card.dailyCadence";

  return (
    <AppLayout
      title={habit.name}
      description={habit.description || t("habitDetail.subtitle")}
      actions={[
        {
          name: t("common.back"),
          icon: <ArrowLeft className="h-4 w-4" />,
          onClick: () => navigate(-1),
        },
      ]}
    >
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Top Row: Info + Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-2xl">{habit.name}</CardTitle>
                  {habit.description && (
                    <CardDescription className="mt-2">
                      {habit.description}
                    </CardDescription>
                  )}
                </div>
                <ShareDialog
                  itemId={habit.id}
                  type="habit"
                  itemTitle={habit.name}
                  trigger={
                    <Button variant="outline" size="sm">
                      <Share2 className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t(cadenceLabelKey)}
                </Badge>
                {habit.reminderTime && (
                  <Badge variant="secondary">
                    {habit.reminderTime} · {habit.cadence || t("habits.card.dailyCadence")}
                  </Badge>
                )}
                {habit.targetPerWeek && (
                  <Badge variant="secondary">
                    {habit.targetPerWeek}x / {t("plan.week")}
                  </Badge>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">
                    {t("habits.card.thisWeek")}
                  </span>
                  <span className="font-medium">
                    {thisWeekCount} / {habit.targetPerWeek || 7}
                  </span>
                </div>
                <Progress value={completionRate} />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-500" />
                  {t("habitDetail.stats.streak")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{streak}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  {t("habitDetail.stats.thisWeek")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {thisWeekCount}/{habit.targetPerWeek || 7}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                  {t("habitDetail.stats.completion")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {Math.round(completionRate)}%
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Activity history */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              {t("habitDetail.activity.title")}
            </CardTitle>
            <CardDescription>
              {t("habitDetail.activity.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activityDays.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("habitDetail.activity.empty")}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {activityDays.map((day) => {
                  const hasCheckIn = hasCheckInOnDate(day);
                  const isToday = isSameDay(day, today);
                  return (
                    <div
                      key={day.toISOString()}
                      className={[
                        "w-6 h-6 rounded-md flex items-center justify-center border",
                        hasCheckIn
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted text-muted-foreground border-border",
                        !hasCheckIn && isToday
                          ? "border-primary"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {hasCheckIn && <Flame className="h-3 w-3" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Check-in */}
        <div className="flex justify-end">
          <Button
            size="lg"
            onClick={() => checkInMutation.mutate()}
            disabled={checkInMutation.isPending}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {checkInMutation.isPending
              ? t("habitDetail.checkIn.pending")
              : t("habitDetail.checkIn.cta")}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}


